import React from 'react';
import { Card, Table, Typography, Space, Tag, Modal, Descriptions, Select, message } from 'antd';
import { BankOutlined, CreditCardOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface Donation {
    id: string;
    amount: number;
    frequency: string;
    paymentMethod: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address: string;
    zip: string;
    city: string;
    country: string;
    message?: string;
    wantsReceipt: boolean;
    status: string;
    createdAt: string;
    privacyConsent: boolean;
    privacyConsentDate: string;
    sepaMandateConsent: boolean;
    sepaAccountHolder?: string;
    sepaIban?: string;
    sepaBic?: string;
    sepaBankCode?: string;
}

const DonationsList: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [selectedDonation, setSelectedDonation] = React.useState<Donation | null>(null);
    const [updatingId, setUpdatingId] = React.useState<string | null>(null);
    const [messageApi, contextHolder] = message.useMessage();

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ['adminDonations', page, pageSize],
        queryFn: async () => {
            const response = await apiClient.get('/admin/donate/donations', {
                params: { pageNumber: page, pageSize }
            });
            return response.data;
        }
    });

    const updateStatus = async (id: string, newStatus: string) => {
        setUpdatingId(id);
        try {
            await apiClient.put('/admin/donate/donations/status', { id, status: newStatus });
            messageApi.success(t('status_updated', 'Status wurde aktualisiert'));
            queryClient.invalidateQueries({ queryKey: ['adminDonations'] });
            
            if (selectedDonation && selectedDonation.id === id) {
                setSelectedDonation({ ...selectedDonation, status: newStatus });
            }
        } catch (error) {
            messageApi.error(t('update_failed', 'Aktualisierung fehlgeschlagen'));
        } finally {
            setUpdatingId(null);
        }
    };

    const columns = [
        {
            title: t('date', 'Datum'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text: string) => dayjs(text).format('DD.MM.YYYY HH:mm'),
        },
        {
            title: t('name', 'Name'),
            key: 'name',
            render: (_: unknown, record: Donation) => `${record.firstName} ${record.lastName}`,
        },
        {
            title: t('amount', 'Betrag'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number, record: Donation) => (
                <Text strong>{amount} € <Text type="secondary" className="text-xs">({t(record.frequency, record.frequency)})</Text></Text>
            ),
        },
        {
            title: t('payment_method', 'Zahlungsweise'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            render: (method: string) => (
                <Tag icon={method === 'sepa' ? <BankOutlined /> : <CreditCardOutlined />} color={method === 'sepa' ? 'blue' : 'green'}>
                    {method === 'sepa' ? t('sepa_direct_debit', 'SEPA-Lastschrift') : t('bank_transfer', 'Überweisung')}
                </Tag>
            ),
        },
        {
            title: t('status', 'Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: string, record: Donation) => (
                <Select 
                    value={status} 
                    style={{ width: 140 }} 
                    size="small"
                    loading={updatingId === record.id}
                    onChange={(newStatus) => updateStatus(record.id, newStatus)}
                    options={[
                        { value: 'Pending', label: t('status_pending', 'Ausstehend') },
                        { value: 'Processed', label: t('status_processed', 'Verarbeitet') },
                        { value: 'Cancelled', label: t('status_cancelled', 'Storniert') }
                    ]}
                />
            ),
        },
        {
            title: t('receipt', 'Bescheinigung'),
            dataIndex: 'wantsReceipt',
            key: 'wantsReceipt',
            render: (wants: boolean) => (
                <Tag color={wants ? 'purple' : 'default'}>
                    {wants ? t('yes', 'Ja') : t('no', 'Nein')}
                </Tag>
            ),
        },
        {
            title: t('actions', 'Aktionen'),
            key: 'actions',
            render: (_: unknown, record: Donation) => (
                <a onClick={() => setSelectedDonation(record)}><EyeOutlined /> {t('details', 'Details')}</a>
            ),
        },
    ];

    return (
        <Card className="shadow-sm border-none">
            {contextHolder}
            <Title level={4} className="mb-6">{t('donations_received', 'Eingegangene Spenden')}</Title>
            
            <Table 
                columns={columns} 
                dataSource={pagedData?.data || []} 
                rowKey="id"
                loading={isLoading}
                rowClassName={(record) => {
                    if (record.status === 'Pending') return 'bg-orange-100/30';
                    if (record.status === 'Processed') return 'bg-green-100/30';
                    if (record.status === 'Cancelled') return 'bg-red-100/30';
                    return '';
                }}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: pagedData?.totalRecords || 0,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); }
                }}
            />

            <Modal
                title={t('donation_details', 'Spenden-Details')}
                open={!!selectedDonation}
                onCancel={() => setSelectedDonation(null)}
                footer={null}
                width={700}
            >
                {selectedDonation && (
                    <Space direction="vertical" className="w-full" size="large">
                        <Descriptions title={t('donor_contact', 'Spender & Kontakt')} column={2} bordered size="small">
                            <Descriptions.Item label={t('firstname', 'Vorname')}>{selectedDonation.firstName}</Descriptions.Item>
                            <Descriptions.Item label={t('lastname', 'Nachname')}>{selectedDonation.lastName}</Descriptions.Item>
                            <Descriptions.Item label={t('email', 'E-Mail')}>{selectedDonation.email}</Descriptions.Item>
                            <Descriptions.Item label={t('phone', 'Telefon')}>{selectedDonation.phone || '-'}</Descriptions.Item>
                        </Descriptions>

                        <Descriptions title={t('donation_amount_method', 'Spendenbetrag & Methode')} column={2} bordered size="small">
                            <Descriptions.Item label={t('amount', 'Betrag')}><Text strong className="text-lg">{selectedDonation.amount} €</Text></Descriptions.Item>
                            <Descriptions.Item label={t('frequency', 'Zahlungsweise')}>{t(selectedDonation.frequency, selectedDonation.frequency)}</Descriptions.Item>
                            <Descriptions.Item label={t('method', 'Methode')}>{selectedDonation.paymentMethod === 'sepa' ? t('sepa_direct_debit', 'SEPA-Lastschrift') : t('bank_transfer', 'Überweisung')}</Descriptions.Item>
                            <Descriptions.Item label={t('status', 'Status')}>
                                <Tag color={selectedDonation.status === 'Pending' ? 'orange' : selectedDonation.status === 'Processed' ? 'green' : 'red'}>
                                    {selectedDonation.status}
                                </Tag>
                            </Descriptions.Item>
                        </Descriptions>

                        <Descriptions title={t('billing_address_title', 'Anschrift (Quittung)')} column={1} bordered size="small">
                            <Descriptions.Item label={t('receipt_requested', 'Bescheinigung gewünscht')}>
                                <Tag color={selectedDonation.wantsReceipt ? 'purple' : 'default'}>
                                    {selectedDonation.wantsReceipt ? t('yes', 'Ja') : t('no', 'Nein')}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('street', 'Straße')}>{selectedDonation.address}</Descriptions.Item>
                            <Descriptions.Item label={t('city', 'Stadt')}>{selectedDonation.zip} {selectedDonation.city}</Descriptions.Item>
                            <Descriptions.Item label={t('country', 'Land')}>{selectedDonation.country}</Descriptions.Item>
                        </Descriptions>

                        {selectedDonation.message && (
                            <Descriptions title={t('message_optional', 'Nachricht')} column={1} bordered size="small">
                                <Descriptions.Item label={t('text', 'Text')}>{selectedDonation.message}</Descriptions.Item>
                            </Descriptions>
                        )}

                        {selectedDonation.paymentMethod === 'sepa' && (
                            <Descriptions title={t('sepa_details', 'SEPA-Lastschrift Details')} column={1} bordered size="small">
                                <Descriptions.Item label={t('account_holder', 'Kontoinhaber')}>{selectedDonation.sepaAccountHolder}</Descriptions.Item>
                                <Descriptions.Item label="IBAN">{selectedDonation.sepaIban}</Descriptions.Item>
                                <Descriptions.Item label="BIC">{selectedDonation.sepaBic}</Descriptions.Item>
                                <Descriptions.Item label={t('bank_code', 'Bankleitzahl')}>{selectedDonation.sepaBankCode}</Descriptions.Item>
                                <Descriptions.Item label={t('sepa_mandate', 'SEPA-Mandat')}>
                                    <Tag color={selectedDonation.sepaMandateConsent ? "success" : "default"}>
                                        {selectedDonation.sepaMandateConsent ? t('accepted', 'Akzeptiert') : t('missing', 'Fehlt')}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        )}
                        
                        <Descriptions title={t('gdpr_logs', 'GDPR Logs')} column={1} bordered size="small">
                            <Descriptions.Item label={t('privacy_acceptance', 'Datenschutz Akzeptanz')}>
                                <Tag color={selectedDonation.privacyConsent ? "success" : "default"}>
                                    {selectedDonation.privacyConsent ? t('accepted', 'Akzeptiert') : t('missing', 'Fehlt')}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('consent_date', 'Zustimmungs-Datum')}>
                                {dayjs(selectedDonation.privacyConsentDate).format('DD.MM.YYYY HH:mm:ss')}
                            </Descriptions.Item>
                        </Descriptions>
                    </Space>
                )}
            </Modal>
        </Card>
    );
};

export default DonationsList;
